/**
 * Timezone Context
 * Provides timezone-aware date/time formatting and conversion utilities
 * Automatically detects user's timezone and persists preference
 */

import React, { createContext, useContext, useEffect, useState, ReactNode } from 'react';

interface TimezoneContextType {
  /** Current timezone identifier (IANA format, e.g., "America/New_York") */
  timezone: string;
  
  /** Update user's timezone preference */
  setTimezone: (tz: string) => void;
  
  /** Format UTC date/time to user's local timezone */
  formatDateTime: (utcDate: string | Date, options?: Intl.DateTimeFormatOptions) => string;
  
  /** Format UTC date only (no time) in user's timezone */
  formatDate: (utcDate: string | Date) => string;
  
  /** Format UTC time only (no date) in user's timezone */
  formatTime: (utcDate: string | Date, use24Hour?: boolean) => string;
  
  /** Convert local Date to UTC Date */
  toUTC: (localDate: Date) => Date;
  
  /** Convert UTC string/Date to local Date object */
  fromUTC: (utcDate: string | Date) => Date;
  
  /** Format relative time (e.g., "2 hours ago") */
  formatRelative: (utcDate: string | Date) => string;
  
  /** Get current time in user's timezone */
  now: () => Date;
}

const TimezoneContext = createContext<TimezoneContextType | undefined>(undefined);

interface TimezoneProviderProps {
  children: ReactNode;
}

export const TimezoneProvider: React.FC<TimezoneProviderProps> = ({ children }) => {
  // Auto-detect user's timezone from browser first; fall back to stored preference or UTC
  const [timezone, setTimezoneState] = useState<string>(() => {
    try {
      // Browser detection is always the primary source — never stale
      const browserTz = Intl.DateTimeFormat().resolvedOptions().timeZone;
      if (browserTz) return browserTz;
    } catch { /* ignore */ }
    // Only fall back to localStorage when browser detection fails
    const stored = localStorage.getItem('user_timezone');
    if (stored) return stored;
    return 'UTC';
  });

  // Persist timezone changes to localStorage
  useEffect(() => {
    localStorage.setItem('user_timezone', timezone);
    console.log(`🌍 Timezone set to: ${timezone}`);
  }, [timezone]);

  const setTimezone = (tz: string) => {
    try {
      // Validate timezone by attempting to use it
      Intl.DateTimeFormat('en-US', { timeZone: tz }).format(new Date());
      setTimezoneState(tz);
    } catch (error) {
      console.error(`Invalid timezone: ${tz}`, error);
      // Fallback to UTC if invalid
      setTimezoneState('UTC');
    }
  };

  /**
   * Format UTC date/time to user's local timezone
   */
  const formatDateTime = (utcDate: string | Date, options?: Intl.DateTimeFormatOptions): string => {
    try {
      const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
      
      // Check if date is valid
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      const defaultOptions: Intl.DateTimeFormatOptions = {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true,
        ...options
      };

      return date.toLocaleString('en-US', defaultOptions);
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  /**
   * Format date only (no time)
   */
  const formatDate = (utcDate: string | Date): string => {
    try {
      const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
      
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      return date.toLocaleDateString('en-US', {
        timeZone: timezone,
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'Invalid Date';
    }
  };

  /**
   * Format time only (no date)
   */
  const formatTime = (utcDate: string | Date, use24Hour: boolean = false): string => {
    try {
      const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
      
      if (isNaN(date.getTime())) {
        return 'Invalid Time';
      }

      return date.toLocaleTimeString('en-US', {
        timeZone: timezone,
        hour: '2-digit',
        minute: '2-digit',
        hour12: !use24Hour
      });
    } catch (error) {
      console.error('Time formatting error:', error);
      return 'Invalid Time';
    }
  };

  /**
   * Convert local Date to UTC Date
   * Note: In JavaScript, Date objects are always in local timezone
   * This creates a new Date representing the same moment in UTC
   */
  const toUTC = (localDate: Date): Date => {
    return new Date(localDate.toISOString());
  };

  /**
   * Convert UTC string/Date to local Date object
   * The returned Date object will display in local timezone
   */
  const fromUTC = (utcDate: string | Date): Date => {
    const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
    return new Date(date.toLocaleString('en-US', { timeZone: timezone }));
  };

  /**
   * Format relative time (e.g., "2 hours ago", "in 3 days")
   */
  const formatRelative = (utcDate: string | Date): string => {
    try {
      const date = typeof utcDate === 'string' ? new Date(utcDate) : utcDate;
      
      if (isNaN(date.getTime())) {
        return 'Invalid Date';
      }

      const now = new Date();
      const diffMs = now.getTime() - date.getTime();
      const diffSecs = Math.floor(diffMs / 1000);
      const diffMins = Math.floor(diffMs / 60000);
      const diffHours = Math.floor(diffMs / 3600000);
      const diffDays = Math.floor(diffMs / 86400000);
      const diffWeeks = Math.floor(diffMs / 604800000);
      const diffMonths = Math.floor(diffMs / 2592000000);
      const diffYears = Math.floor(diffMs / 31536000000);

      // Future dates
      if (diffMs < 0) {
        const absDiffMins = Math.abs(diffMins);
        const absDiffHours = Math.abs(diffHours);
        const absDiffDays = Math.abs(diffDays);
        
        if (absDiffMins < 60) return `in ${absDiffMins} minute${absDiffMins !== 1 ? 's' : ''}`;
        if (absDiffHours < 24) return `in ${absDiffHours} hour${absDiffHours !== 1 ? 's' : ''}`;
        if (absDiffDays < 7) return `in ${absDiffDays} day${absDiffDays !== 1 ? 's' : ''}`;
        return formatDate(date);
      }

      // Past dates
      if (diffSecs < 60) return 'just now';
      if (diffMins < 60) return `${diffMins} minute${diffMins > 1 ? 's' : ''} ago`;
      if (diffHours < 24) return `${diffHours} hour${diffHours > 1 ? 's' : ''} ago`;
      if (diffDays < 7) return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`;
      if (diffWeeks < 4) return `${diffWeeks} week${diffWeeks > 1 ? 's' : ''} ago`;
      if (diffMonths < 12) return `${diffMonths} month${diffMonths > 1 ? 's' : ''} ago`;
      return `${diffYears} year${diffYears > 1 ? 's' : ''} ago`;
    } catch (error) {
      console.error('Relative time formatting error:', error);
      return 'some time ago';
    }
  };

  /**
   * Get current time in user's timezone
   */
  const now = (): Date => {
    return new Date();
  };

  const value: TimezoneContextType = {
    timezone,
    setTimezone,
    formatDateTime,
    formatDate,
    formatTime,
    toUTC,
    fromUTC,
    formatRelative,
    now
  };

  return (
    <TimezoneContext.Provider value={value}>
      {children}
    </TimezoneContext.Provider>
  );
};

/**
 * Hook to access timezone utilities
 * Must be used within TimezoneProvider
 */
export const useTimezone = (): TimezoneContextType => {
  const context = useContext(TimezoneContext);
  if (!context) {
    throw new Error('useTimezone must be used within TimezoneProvider');
  }
  return context;
};

/**
 * Common timezone options for selection dropdowns
 */
export const commonTimezones = [
  { value: 'UTC', label: 'UTC (GMT+0)', offset: '+00:00' },
  { value: 'America/New_York', label: 'Eastern Time (GMT-5)', offset: '-05:00' },
  { value: 'America/Chicago', label: 'Central Time (GMT-6)', offset: '-06:00' },
  { value: 'America/Denver', label: 'Mountain Time (GMT-7)', offset: '-07:00' },
  { value: 'America/Los_Angeles', label: 'Pacific Time (GMT-8)', offset: '-08:00' },
  { value: 'Europe/London', label: 'London (GMT+0)', offset: '+00:00' },
  { value: 'Europe/Paris', label: 'Paris (GMT+1)', offset: '+01:00' },
  { value: 'Asia/Dubai', label: 'Dubai (GMT+4)', offset: '+04:00' },
  { value: 'Asia/Kolkata', label: 'India (GMT+5:30)', offset: '+05:30' },
  { value: 'Asia/Shanghai', label: 'China (GMT+8)', offset: '+08:00' },
  { value: 'Asia/Tokyo', label: 'Japan (GMT+9)', offset: '+09:00' },
  { value: 'Australia/Sydney', label: 'Sydney (GMT+10)', offset: '+10:00' },
];
